import { useState } from "react";

const projectTypes = ["Residential — New Construction", "Residential — Renovation", "Multi-Family / Development", "Hospitality — Hotel or Resort", "Hospitality — Restaurant or Bar", "Commercial / Mixed-Use", "Other"];
const budgetRanges = ["Under $250K", "$250K – $500K", "$500K – $1M", "$1M – $3M", "$3M – $10M", "$10M+", "Not sure yet"];
const timelines = ["Ready to start now", "Within 3 months", "3–6 months", "6–12 months", "Exploring / planning stage"];

export default function ContactForm() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const msg = (form.querySelector("[name=message]") as HTMLTextAreaElement)?.value || "";
    if (msg.length > 500) return;
    setLoading(true);
    const data = new FormData(form);
    const encoded = new URLSearchParams();
    data.forEach((val, key) => encoded.append(key, val as string));
    try {
      await fetch("https://readdy.ai/api/form/d6srmlid1d31f3q965bg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encoded.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full border border-arch-border rounded-md px-4 py-3 text-sm text-arch-charcoal font-light focus:outline-none focus:border-arch-charcoal transition-colors bg-arch-cream placeholder-arch-light";

  return (
    <section className="py-24 lg:py-32 px-8 lg:px-14 bg-white">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-14 lg:gap-20">
        {/* Left info */}
        <div className="lg:col-span-2">
          <p className="text-arch-light text-xs tracking-[0.2em] uppercase mb-3">Get In Touch</p>
          <h2 className="font-serif text-4xl text-arch-charcoal font-light leading-tight mb-6">
            Start Your<br /><em>Project</em>
          </h2>
          <p className="text-arch-gray text-sm font-light leading-relaxed mb-10 max-w-xs">
            Tell us about your project. We&apos;ll schedule a complimentary consultation — at our Santa Monica studio or via video call.
          </p>
          <div className="space-y-6">
            {[
              { icon: "ri-map-pin-2-line", label: "Visit Us", value: "1323 Montana Ave, Suite 200\nSanta Monica, CA 90403" },
              { icon: "ri-phone-line", label: "Call Us", value: "(310) 555-1323" },
              { icon: "ri-mail-line", label: "Email", value: "hello@studio1323.com" },
            ].map((item) => (
              <div key={item.label} className="flex items-start gap-4">
                <div className="w-9 h-9 flex items-center justify-center flex-shrink-0 border border-arch-border rounded-md">
                  <i className={`${item.icon} text-arch-charcoal text-base`} />
                </div>
                <div>
                  <p className="text-arch-light text-xs uppercase tracking-wider mb-0.5">{item.label}</p>
                  <p className="text-arch-charcoal text-sm font-light whitespace-pre-line">{item.value}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Trust badge */}
          <div className="mt-10 pt-8 border-t border-arch-border">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 flex items-center justify-center border border-arch-border rounded-md flex-shrink-0">
                <i className="ri-shield-check-line text-arch-charcoal text-base" />
              </div>
              <div>
                <p className="text-arch-charcoal text-xs font-semibold tracking-wide mb-1">Complimentary Consultation</p>
                <p className="text-arch-gray text-xs font-light leading-relaxed">No fees, no obligation. We respond within one business day.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="lg:col-span-3">
          {submitted ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-20">
              <div className="w-14 h-14 flex items-center justify-center border border-arch-border rounded-full mb-6">
                <i className="ri-check-line text-arch-charcoal text-2xl" />
              </div>
              <h3 className="font-serif text-2xl text-arch-charcoal font-light mb-3">Message Received</h3>
              <p className="text-arch-gray text-sm font-light max-w-xs leading-relaxed">
                Thank you for reaching out. We&apos;ll be in touch within one business day to schedule your complimentary consultation.
              </p>
            </div>
          ) : (
            <form id="consultation-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
              {/* Row 1 — Name + Email */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-arch-gray text-xs tracking-wider uppercase block mb-1.5">Full Name *</label>
                  <input type="text" name="name" required placeholder="Jane Smith" className={inputClass} />
                </div>
                <div>
                  <label className="text-arch-gray text-xs tracking-wider uppercase block mb-1.5">Email *</label>
                  <input type="email" name="email" required placeholder="jane@example.com" className={inputClass} />
                </div>
              </div>

              {/* Row 2 — Phone + Project Type */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-arch-gray text-xs tracking-wider uppercase block mb-1.5">Phone</label>
                  <input type="tel" name="phone" placeholder="(310) 000-0000" className={inputClass} />
                </div>
                <div>
                  <label className="text-arch-gray text-xs tracking-wider uppercase block mb-1.5">Project Type *</label>
                  <select name="project_type" required className={`${inputClass} cursor-pointer`}>
                    <option value="">Select type...</option>
                    {projectTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>

              {/* Row 3 — Budget + Timeline */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-arch-gray text-xs tracking-wider uppercase block mb-1.5">Budget Range</label>
                  <select name="budget_range" className={`${inputClass} cursor-pointer`}>
                    <option value="">Select range...</option>
                    {budgetRanges.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-arch-gray text-xs tracking-wider uppercase block mb-1.5">Timeline</label>
                  <select name="timeline" className={`${inputClass} cursor-pointer`}>
                    <option value="">Select timeline...</option>
                    {timelines.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>

              {/* Location */}
              <div>
                <label className="text-arch-gray text-xs tracking-wider uppercase block mb-1.5">Project Location</label>
                <input type="text" name="location" placeholder="Santa Monica, CA" className={inputClass} />
              </div>

              {/* Message */}
              <div>
                <label className="text-arch-gray text-xs tracking-wider uppercase block mb-1.5">
                  Project Description *
                  <span className={`ml-2 font-normal normal-case tracking-normal ${charCount > 450 ? "text-red-500" : ""}`}>
                    ({charCount}/500)
                  </span>
                </label>
                <textarea
                  name="message" required rows={4} maxLength={500}
                  placeholder="Describe your project scope, vision, site, and any specific requirements..."
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className={`${inputClass} resize-none`}
                />
              </div>

              <button
                type="submit" disabled={loading}
                className="w-full bg-arch-charcoal text-white py-4 text-xs tracking-[0.18em] uppercase font-semibold hover:bg-arch-black transition-all rounded-md cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {loading ? "Sending..." : "Send Consultation Request"}
              </button>
              <p className="text-arch-light text-xs text-center font-light">
                Free initial consultation · No commitment required · We respond within 1 business day
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
